import React, { useCallback } from 'react';
import { Flex, Grid, GridItem } from '@televet/kibble-ui/build/chakra';
import { TimePicker } from '@televet/kibble-ui/build/components/TimePicker';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Switch } from '@televet/kibble-ui/build/components/Switch';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { Popover } from '@televet/kibble-ui/build/components/Popover';
import { formatPluralization } from 'shared/utils';

export interface BatchingSchedule {
  id: string;
  dayOfWeek: number;
  endTime: Date;
}

interface EODScheduleProps {
  label: string;
  value: number;
  schedule?: BatchingSchedule;
  onUpdateSchedule: (schedule: BatchingSchedule) => void;
  onToggleSchedule: (dayOfWeek: number) => void;
  onApplyTimeToAllDays: (endTime: Date) => void;
}

export const EODSchedule = ({
  label,
  value,
  schedule,
  onApplyTimeToAllDays,
  onUpdateSchedule,
  onToggleSchedule,
}: EODScheduleProps): JSX.Element => {
  const isScheduleDisabled = !schedule;

  const handleUpdateSchedule = useCallback(
    (time: Date) => {
      // Default date to epoch time
      const endTime = new Date(0);
      endTime.setHours(time.getHours());
      endTime.setMinutes(time.getMinutes());
      endTime.setSeconds(0);
      endTime.setMilliseconds(0);

      if (schedule) {
        onUpdateSchedule({ ...schedule, endTime });
      }
    },
    [onUpdateSchedule, schedule],
  );

  return (
    <Grid role="group" className="Schedule__Day" alignItems="center" templateColumns="repeat(6, 1fr)" gap={4}>
      <GridItem colSpan={3}>
        <Popover
          anchor={
            <Flex alignItems="center" gap={2}>
              {isScheduleDisabled && (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  bgColor="background.accentSubtle"
                  py={1}
                  px={2}
                  borderRadius="base"
                >
                  <Icon name="arrowDown" size="sm" />
                </Flex>
              )}
              <Text variant={isScheduleDisabled ? 'subtle' : 'default'}>{label}</Text>
            </Flex>
          }
          placement="top"
          trigger="hover"
          size="lg"
          {...(!isScheduleDisabled && { isOpen: false })}
        >
          <Text size="sm" variant="onContrast">
            Transactions from{' '}
            <Text as="span" size="sm" fontWeight="semibold" variant="onContrast">
              {formatPluralization({ word: label })}
            </Text>{' '}
            will be included in the end of day report for the next Payout Day.
          </Text>
        </Popover>
      </GridItem>
      <GridItem colSpan={2}>
        <Flex gap={1}>
          <TimePicker
            key={`${schedule?.endTime?.toISOString()}-${value}`}
            inputProps={{
              rightElement: undefined,
              isDisabled: isScheduleDisabled,
              placeholder: '--',
            }}
            placement="bottom"
            initialTime={schedule?.endTime}
            onTimeChange={handleUpdateSchedule}
          />
          <Tooltip label="Apply time to all days">
            <Button
              iconName="bulkSelect"
              variant="ghost"
              visibility="hidden"
              {...(!isScheduleDisabled && {
                _groupHover: {
                  visibility: 'visible',
                },
              })}
              onClick={(): void => {
                if (schedule) {
                  onApplyTimeToAllDays(schedule.endTime);
                }
              }}
            />
          </Tooltip>
        </Flex>
      </GridItem>
      <GridItem>
        <Switch isChecked={!!schedule} onChange={(): void => onToggleSchedule(value)} />
      </GridItem>
    </Grid>
  );
};
